/**
 * /status — public availability widget (NFR-096, R1.9 sub-step 5).
 *
 * Read-only page anyone (logged in or not) can hit to see whether the
 * API + web bundle are healthy. The self-hosted Uptime Kuma instance
 * (`infra/docker/monitoring/docker-compose.yml`, deployed at
 * `https://status.sleepercising.com`) watches the same `/health`
 * endpoint at 60-second intervals; the SLA is 99.5% rolling 30-day
 * uptime per the NFR-096 target.
 *
 * The page hits `/api/v1/health` once on mount + every 30 s thereafter.
 * On error it shows `degraded` (instead of failing silently). Pilot
 * scope; richer per-component breakdown lands when we wire individual
 * health checks (DB / Redis / SendGrid).
 *
 * @see NFR-096 · ADR-0008 · infra/runbooks/monitoring.md
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const POLL_INTERVAL_MS = 30_000;

type Status = 'loading' | 'operational' | 'degraded';

interface HealthEnvelope {
  status?: string;
  region?: string;
  uptime?: number;
}

export function StatusPage(): JSX.Element {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>('loading');
  const [envelope, setEnvelope] = useState<HealthEnvelope | null>(null);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function probe(): Promise<void> {
      try {
        const res = await fetch('/api/v1/health', {
          headers: { Accept: 'application/json' },
        });
        if (!active) return;
        if (res.ok) {
          const body = (await res.json()) as HealthEnvelope;
          setEnvelope(body);
          setStatus(body.status === 'ok' ? 'operational' : 'degraded');
        } else {
          setStatus('degraded');
        }
      } catch {
        if (active) setStatus('degraded');
      } finally {
        if (active) {
          setLastCheckedAt(new Date());
          timer = setTimeout(() => {
            void probe();
          }, POLL_INTERVAL_MS);
        }
      }
    }

    void probe();
    return (): void => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const tone =
    status === 'operational'
      ? 'bg-[hsl(var(--surface-success-bg,142_70%_94%))] text-[hsl(var(--surface-success-fg,142_60%_24%))]'
      : status === 'degraded'
        ? 'bg-[hsl(var(--surface-danger-bg,355_70%_94%))] text-[hsl(var(--surface-danger-fg,355_60%_30%))]'
        : 'bg-bone-2 text-ink/70';

  return (
    <div className="mx-auto max-w-2xl p-6">
      <h1 className="font-display text-3xl font-semibold text-ink">{t('status.title')}</h1>
      <p className="mt-2 text-sm text-ink/70">{t('status.body')}</p>

      <div className={`mt-6 rounded-xl p-5 ${tone}`}>
        <div className="text-xs font-medium uppercase tracking-wide opacity-80">
          {t('status.indicator.label')}
        </div>
        <div className="mt-1 font-display text-xl font-semibold">
          {t(`status.indicator.${status}`)}
        </div>
        {envelope?.region ? (
          <div className="mt-2 text-xs opacity-70">
            {t('status.region')}: <span className="font-mono">{envelope.region}</span>
          </div>
        ) : null}
      </div>

      <div className="mt-4 text-xs text-ink/60">
        {lastCheckedAt
          ? `${t('status.lastChecked')}: ${lastCheckedAt.toLocaleTimeString()}`
          : t('status.checking')}
      </div>

      <div className="mt-8 rounded-lg border border-bone-3 p-4 text-sm">
        <div className="font-medium text-ink">{t('status.sla.title')}</div>
        <div className="mt-1 text-ink/70">{t('status.sla.body')}</div>
      </div>
    </div>
  );
}
